import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from 'components/layout';
import invoiceService from 'services/invoiceService';
import { FilePreviewModal } from './provider/FilePreviewModal';
import Swal from 'sweetalert2';
import './UpdateInvoiceView.css';

export const UpdateInvoiceView = () => {
    const { invoiceId } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [invoice, setInvoice] = useState(null);

    const [files, setFiles] = useState({
        xml: null,
        pdf: null,
        factura_firmada: null,
        orden_compra: null
    });

    const [filePreviews, setFilePreviews] = useState({
        xml: null,
        pdf: null,
        factura_firmada: null,
        orden_compra: null
    });

    const [showPreview, setShowPreview] = useState(false);
    const [previewFile, setPreviewFile] = useState(null);

    useEffect(() => {
        loadInvoice();
    }, [invoiceId]);

    const loadInvoice = async () => {
        try {
            setLoading(true);
            const response = await invoiceService.getInvoiceById(invoiceId);

            if (!response.error) {
                setInvoice(response.data.data);
            } else {
                throw new Error('No se pudo cargar la factura');
            }
        } catch (error) {
            console.error('Error al cargar factura:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'No se pudo cargar la información de la factura',
                timer: 3000,
                showConfirmButton: false
            });
            navigate(-1); // Regresar a la vista anterior
        } finally {
            setLoading(false);
        }
    };

    const handleFileChange = (e, fileType) => {
        const file = e.target.files[0];

        if (!file) return;

        // Validar tipo de archivo
        const validExtensions = {
            xml: ['.xml'],
            pdf: ['.pdf'],
            factura_firmada: ['.pdf'],
            orden_compra: ['.pdf']
        };

        const extension = '.' + file.name.split('.').pop().toLowerCase();

        if (!validExtensions[fileType].includes(extension)) {
            Swal.fire({
                icon: 'error',
                title: 'Archivo inválido',
                text: `El archivo debe ser ${validExtensions[fileType].join(' o ')}`,
                timer: 3000
            });
            return;
        }

        // Guardar archivo
        setFiles(prev => ({ ...prev, [fileType]: file }));

        // Crear preview si es PDF
        if (extension === '.pdf') {
            const reader = new FileReader();
            reader.onload = (event) => {
                setFilePreviews(prev => ({
                    ...prev,
                    [fileType]: { url: event.target.result, name: file.name, type: 'pdf' }
                }));
            };
            reader.readAsDataURL(file);
        } else if (extension === '.xml') {
            const reader = new FileReader();
            reader.onload = (event) => {
                setFilePreviews(prev => ({
                    ...prev,
                    [fileType]: { content: event.target.result, name: file.name, type: 'xml' }
                }));
            };
            reader.readAsText(file);
        }
    };

    const handleRemoveFile = (fileType) => {
        setFiles(prev => ({ ...prev, [fileType]: null }));
        setFilePreviews(prev => ({ ...prev, [fileType]: null }));
    };

    const handlePreview = (fileType) => {
        if (filePreviews[fileType]) {
            setPreviewFile(filePreviews[fileType]);
            setShowPreview(true);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validar que al menos un archivo fue seleccionado
        const hasFiles = Object.values(files).some(file => file !== null);

        if (!hasFiles) {
            Swal.fire({
                icon: 'warning',
                title: 'Sin cambios',
                text: 'Debes seleccionar al menos un archivo para actualizar',
                timer: 3000
            });
            return;
        }

        // Confirmar actualización
        const result = await Swal.fire({
            title: '¿Actualizar factura?',
            text: 'La factura será enviada nuevamente a revisión',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Sí, actualizar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#28a745',
            cancelButtonColor: '#6c757d'
        });

        if (!result.isConfirmed) return;

        setSaving(true);

        try {
            const formData = new FormData();

            // Agregar archivos
            if (files.xml) formData.append('xml', files.xml);
            if (files.pdf) formData.append('pdf', files.pdf);
            if (files.factura_firmada) formData.append('factura_firmada', files.factura_firmada);
            if (files.orden_compra) formData.append('orden_compra', files.orden_compra);

            // Agregar datos de negocio
            formData.append('centro_costo_id', invoice.centro_costo_id);
            formData.append('tipo_gasto_id', invoice.tipo_gasto_id);
            if (invoice.notas) formData.append('notas', invoice.notas);

            const response = await invoiceService.updateInvoice(invoice.id, formData);

            if (!response.error) {
                await Swal.fire({
                    icon: 'success',
                    title: '¡Factura Actualizada!',
                    text: 'La factura ha sido actualizada y será revisada nuevamente',
                    timer: 3000,
                    showConfirmButton: false
                });

                navigate('/invoices'); // Redirigir a la lista de facturas
            } else {
                throw new Error(response.data);
            }
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.message || 'No se pudo actualizar la factura',
                timer: 3000
            });
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        navigate(-1);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('es-MX', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-MX', {
            style: 'currency',
            currency: 'MXN'
        }).format(amount || 0);
    };

    const fileLabels = {
        xml: 'Archivo XML',
        pdf: 'Archivo PDF',
        factura_firmada: 'Factura Firmada (PDF)',
        orden_compra: 'Orden de Compra (PDF) - Opcional'
    };

    const fileIcons = {
        xml: '📄',
        pdf: '📑',
        factura_firmada: '✍️',
        orden_compra: '📋'
    };

    if (loading) {
        return (
            <Layout>
                <div className="container">
                    <div className="update-invoice-loading">
                        <div className="update-invoice-loading-spinner"></div>
                        <span className="update-invoice-loading-text">Cargando factura...</span>
                    </div>
                </div>
            </Layout>
        );
    }

    if (!invoice) {
        return (
            <Layout>
                <div className="container">
                    <div className="update-invoice-loading">
                        <i className="bi bi-exclamation-triangle" style={{ fontSize: '3rem', color: 'var(--color-warning)' }}></i>
                        <span className="update-invoice-loading-text">No se pudo cargar la factura</span>
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="container">
                {/* Header */}
                <div className="update-invoice-header">
                    <div className="update-invoice-header-content">
                        <div className="update-invoice-icon">
                            📄
                        </div>
                        <div className="update-invoice-info">
                            <h1 className="update-invoice-title">Actualizar Factura</h1>
                            <p className="update-invoice-folio">{invoice.folio}</p>
                        </div>
                    </div>
                    <div className="update-invoice-actions">
                        <button
                            className="btn-cancel-header"
                            onClick={handleCancel}
                            disabled={saving}
                        >
                            <i className="bi bi-x-circle"></i>
                            Cancelar
                        </button>
                    </div>
                </div>

                {/* Información de la Factura */}
                <div className="card">
                    <h3 className="card-header">
                        <i className="bi bi-info-circle-fill"></i> Información de la Factura
                    </h3>

                    {invoice.estatus === 'rechazada' && invoice.notas_rechazo && (
                        <div className="rejection-alert">
                            <div className="rejection-alert-icon">⚠️</div>
                            <div className="rejection-alert-content">
                                <strong>Motivo del Rechazo:</strong>
                                <p>{invoice.notas_rechazo}</p>
                            </div>
                        </div>
                    )}

                    <div className="invoice-details-grid">
                        <div className="info-item">
                            <div className="info-label">
                                <i className="bi bi-file-text-fill"></i> UUID
                            </div>
                            <div className="info-value uuid-text">{invoice.uuid}</div>
                        </div>
                        <div className="info-item">
                            <div className="info-label">
                                <i className="bi bi-calendar-event-fill"></i> Fecha
                            </div>
                            <div className="info-value">{formatDate(invoice.fecha)}</div>
                        </div>
                        <div className="info-item">
                            <div className="info-label">
                                <i className="bi bi-cash-stack"></i> Subtotal
                            </div>
                            <div className="info-value">{formatCurrency(invoice.subtotal)}</div>
                        </div>
                        <div className="info-item">
                            <div className="info-label">
                                <i className="bi bi-percent"></i> IVA
                            </div>
                            <div className="info-value">{formatCurrency(invoice.iva)}</div>
                        </div>
                        <div className="info-item">
                            <div className="info-label">
                                <i className="bi bi-currency-dollar"></i> Total
                            </div>
                            <div className="info-value total-amount">{formatCurrency(invoice.total)}</div>
                        </div>
                        <div className="info-item">
                            <div className="info-label">
                                <i className="bi bi-building"></i> Centro de Costo
                            </div>
                            <div className="info-value">{invoice.centro_costo_nombre || 'N/A'}</div>
                        </div>
                        <div className="info-item">
                            <div className="info-label">
                                <i className="bi bi-tag-fill"></i> Tipo de Gasto
                            </div>
                            <div className="info-value">{invoice.tipo_gasto_nombre || 'N/A'}</div>
                        </div>
                        {invoice.notas && (
                            <div className="info-item full-width">
                                <div className="info-label">
                                    <i className="bi bi-chat-left-text-fill"></i> Notas
                                </div>
                                <div className="info-value">{invoice.notas}</div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Formulario de Archivos */}
                <form onSubmit={handleSubmit}>
                    <div className="card">
                        <h3 className="card-header">
                            <i className="bi bi-cloud-upload-fill"></i> Actualizar Archivos
                        </h3>

                        <p className="helper-text">
                            <i className="bi bi-info-circle"></i>
                            Selecciona los archivos que deseas actualizar. La factura será enviada nuevamente a revisión.
                        </p>

                        <div className="files-grid">
                            {Object.keys(files).map(fileType => (
                                <div key={fileType} className="file-upload-card">
                                    <div className="file-upload-header">
                                        <span className="file-type-icon">{fileIcons[fileType]}</span>
                                        <label className="file-type-label">{fileLabels[fileType]}</label>
                                    </div>

                                    {!files[fileType] ? (
                                        <div className="file-upload-area">
                                            <input
                                                type="file"
                                                id={`file-${fileType}`}
                                                accept={fileType === 'xml' ? '.xml' : '.pdf'}
                                                onChange={(e) => handleFileChange(e, fileType)}
                                                className="file-input-hidden"
                                            />
                                            <label htmlFor={`file-${fileType}`} className="file-upload-label">
                                                <i className="bi bi-cloud-arrow-up upload-icon"></i>
                                                <span className="upload-text">Click para seleccionar</span>
                                                <span className="upload-hint">
                                                    {fileType === 'xml' ? 'Archivo .xml' : 'Archivo .pdf'}
                                                </span>
                                            </label>
                                        </div>
                                    ) : (
                                        <div className="file-selected-area">
                                            <div className="file-selected-info">
                                                <i className="bi bi-check-circle-fill file-selected-icon"></i>
                                                <div className="file-selected-details">
                                                    <span className="file-selected-name">{files[fileType].name}</span>
                                                    <span className="file-selected-size">
                                                        {(files[fileType].size / 1024).toFixed(2)} KB
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="file-selected-actions">
                                                <button
                                                    type="button"
                                                    className="btn-file-action btn-preview"
                                                    onClick={() => handlePreview(fileType)}
                                                    title="Vista previa"
                                                >
                                                    <i className="bi bi-eye-fill"></i>
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn-file-action btn-remove"
                                                    onClick={() => handleRemoveFile(fileType)}
                                                    title="Quitar archivo"
                                                >
                                                    <i className="bi bi-trash-fill"></i>
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Acciones del Formulario */}
                    <div className="form-actions-footer">
                        <button
                            type="button"
                            className="btn btn-cancel"
                            onClick={handleCancel}
                            disabled={saving}
                        >
                            <i className="bi bi-x-circle"></i>
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={saving || !Object.values(files).some(f => f !== null)}
                        >
                            {saving ? (
                                <>
                                    <span className="spinner-small"></span>
                                    Actualizando...
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-check-circle-fill"></i>
                                    Actualizar y Enviar a Revisión
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>

            {/* Modal de Vista Previa */}
            {showPreview && previewFile && (
                <FilePreviewModal
                    file={previewFile}
                    onClose={() => setShowPreview(false)}
                />
            )}
        </Layout>
    );
};